import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Typography } from '@mui/material';

import {
  SalesforceUserLicensePoolRequest,
  createSalesforceUserLicenseRequest,
  extendSalesforceUserLicenseRequest,
} from '~/services/SalesforceService';

import { useGetSalesforceUserLicense } from '../hooks/useGetSalesforceUserLicense';

export default function SalesforceAccessRequest() {
  const { loading, userLicensePoolAccessData, error, refresh } = useGetSalesforceUserLicense();
  const { enqueueSnackbar } = useSnackbar();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for real-time countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const salesforceLicensePoolRequest = (): string => userLicensePoolAccessData?.licensePoolRequest?.id || '';

  const endDateTime = (): Date => new Date(userLicensePoolAccessData?.licensePoolRequest?.endDateTime || '');

  const timeRemainingFormatted = (): string => {
    const timeRemaining = endDateTime().getTime() - currentTime.getTime();

    // If time has expired, show 0:0:0
    if (timeRemaining <= 0) {
      return '0:00:00';
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

    // Format with leading zeros for minutes and seconds
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');

    return `${hours}:${formattedMinutes}:${formattedSeconds}`;
  };

  const defaultTime = (): number => userLicensePoolAccessData?.defaultAccessTime * 1000 * 60 * 60; // 1000 * 60 * 60 = 1 hour

  const licensePoolRequest = (): SalesforceUserLicensePoolRequest => ({
    userId: userLicensePoolAccessData.userId,
    startDateTime: userLicensePoolAccessData.licensePoolRequest?.startDateTime || new Date().toISOString(),
    endDateTime: new Date(new Date().getTime() + defaultTime()).toISOString(),
    extensionCount: userLicensePoolAccessData.licensePoolRequest?.extensionCount + 1 || 0,
  });

  const handleRequestAccess = async () => {
    try {
      const response = await createSalesforceUserLicenseRequest({ licenseRequest: licensePoolRequest() });
      if (response.status === 201) {
        enqueueSnackbar('Salesforce Access Granted', { variant: 'success' });
        refresh(); // Refresh the data to show updated license status
      } else {
        enqueueSnackbar('Failed to request Salesforce Access', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to request Salesforce Access', { variant: 'error' });
    }
  };

  const handleExtendAccess = async () => {
    try {
      const response = await extendSalesforceUserLicenseRequest({
        licenseId: salesforceLicensePoolRequest(),
        licenseRequest: licensePoolRequest(),
      });
      if (response.status === 200) {
        enqueueSnackbar('Salesforce Access Extended', { variant: 'success' });
        refresh(); // Refresh the data to show updated license status
      } else {
        enqueueSnackbar('Failed to extend Salesforce Access', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to extend Salesforce Access', { variant: 'error' });
    }
  };

  if (loading || error || !userLicensePoolAccessData) {
    return null;
  }

  return (
    <>
      {!userLicensePoolAccessData.licensePoolRequest && (
        <Button variant="contained" color="primary" onClick={handleRequestAccess} sx={{ mb: 2 }}>
          Request Salesforce Access
        </Button>
      )}

      {userLicensePoolAccessData.licensePoolRequest && (
        <Button variant="contained" color="primary" onClick={handleExtendAccess} sx={{ mb: 2 }}>
          Extend Salesforce Access - {timeRemainingFormatted()} remaining
        </Button>
      )}

      <Typography variant="body2">Please Login to Salesforce using Azure SSO</Typography>
    </>
  );
}
